import {
	AssuredWorkloadOutlined,
	BallotOutlined,
	CalendarMonthOutlined,
	EmojiEventsOutlined,
	InsertChartOutlined,
	LiveTvOutlined,
	NightlifeOutlined,
	PeopleAltOutlined,
  } from "@mui/icons-material";
  import { Box, Grid, Typography } from "@mui/material";
  import React from "react";
  import { Helmet } from "react-helmet";
  import PropTypes from "prop-types";
  import Tabs from "@mui/material/Tabs";
  import Tab from "@mui/material/Tab";
  import api from "../../../services/Axios";
  import toast from "react-hot-toast";
  
  function CustomTabPanel(props) {
	const { children, value, index, ...other } = props;
  
	return (
	  <div
		role="tabpanel"
		hidden={value !== index}
		id={`simple-tabpanel-${index}`}
		aria-labelledby={`simple-tab-${index}`}
		{...other}
	  >
		{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
	  </div>
	);
  }
  
  CustomTabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.number.isRequired,
	value: PropTypes.number.isRequired,
  };
  
  function a11yProps(index) {
	return {
	  id: `simple-tab-${index}`,
	  "aria-controls": `simple-tabpanel-${index}`,
	};
  }
  
  export default function Setting() {
	const [value, setValue] = React.useState(0);
  
	const [referralAmount, setReferralAmount] = React.useState("");
	const [voteUnit, setVoteUnit] = React.useState("");
  
	const [earningType, setEarningType] = React.useState("TIP"); // TIP | REVENUE
	const [earning, setEarning] = React.useState({
	  organizer: "",
	  dj: "",
	  admin: "",
	});
  
	const [earningIds, setEarningIds] = React.useState({
	  TIP: null,
	  REVENUE: null,
	});
  
	const [referralId, setReferralId] = React.useState(null);
	const [voteId, setVoteId] = React.useState(null);
  
	const [errors, setErrors] = React.useState({});
	const [loadingEarning, setLoadingEarning] = React.useState(false);
	const [loadingEarningButton, setLoadingEarningButton] = React.useState(false);
  
	const [loadingReferral, setLoadingReferral] = React.useState(false);
	const [loadingReferralButton, setLoadingReferralButton] = React.useState(false);
  
	const [loadingVote, setLoadingVote] = React.useState(false);
	const [loadingVoteButton, setLoadingVoteButton] = React.useState(false);
  
	const handleChange = (event, newValue) => {
	  setValue(newValue);
	};
  
	const isValidNumber = (val) => val !== "" && !isNaN(val);
  
	const getAuthHeader = () => ({
	  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
	});
  
	// =========================
	// ✅ System Settings APIs (Referral & Vote) - same endpoint, different settingKey
	// =========================
	const GetSystemSettingByKey = async (settingKey) => {
	  const header = getAuthHeader();
	  return api
		.get(`/system-settings?settingKey=${settingKey}`, header)
		.then((response) => response)
		.catch((err) => err?.response);
	};
  
	const UpdateSystemSetting = async (id, payload) => {
	  const header = getAuthHeader();
	  return api
		.patch(`/system-settings/${id}`, payload, header)
		.then((response) => response)
		.catch((err) => err?.response);
	};
  
	const loadReferral = async () => {
	  setLoadingReferral(true);
	  const response = await GetSystemSettingByKey("REFERRAL_AMOUNT");
  
	  if (response?.data?.success) {
		const data = response?.data?.data;
		setReferralId(data?.id || null);
		setReferralAmount(
		  data?.settingValue !== undefined ? String(data.settingValue) : ""
		);
		setErrors((prev) => ({ ...prev, referralApi: "", referralAmount: "" }));
	  } else {
		setErrors((prev) => ({
		  ...prev,
		  referralApi: response?.data?.message || "Something went wrong",
		}));
	  }
	  setLoadingReferral(false);
	};
  
	const loadVote = async () => {
	  setLoadingVote(true);
	  const response = await GetSystemSettingByKey("VOTES_PER_DOLLAR");
  
	  if (response?.data?.success) {
		const data = response?.data?.data;
		setVoteId(data?.id || null);
		setVoteUnit(data?.settingValue !== undefined ? String(data.settingValue) : "");
		setErrors((prev) => ({ ...prev, voteApi: "", voteUnit: "" }));
	  } else {
		setErrors((prev) => ({
		  ...prev,
		  voteApi: response?.data?.message || "Something went wrong",
		}));
	  }
	  setLoadingVote(false);
	};
  
	// =========================
	// Earning APIs (unchanged)
	// =========================
	const GetShareConfigRatio = async (type) => {
	  const header = getAuthHeader();
	  return api
		.get(`/share-config-ratio?type=${type}`, header)
		.then((response) => response)
		.catch((err) => err?.response);
	};
  
	const UpdateShareConfigRatio = async (id, payload) => {
	  const header = getAuthHeader();
	  return api
		.patch(`/share-config-ratio/${id}`, payload, header)
		.then((response) => response)
		.catch((err) => err?.response);
	};
  
	const loadEarningForType = async (type) => {
	  setLoadingEarning(true);
	  const response = await GetShareConfigRatio(type);
  
	  if (response?.data?.success) {
		const data = response?.data?.data;
  
		setEarningIds((prev) => ({
		  ...prev,
		  [type]: data?.id || null,
		}));
  
		setEarning({
		  organizer:
			data?.organizerShare !== undefined ? String(data.organizerShare) : "",
		  dj: data?.djShare !== undefined ? String(data.djShare) : "",
		  admin: data?.adminShare !== undefined ? String(data.adminShare) : "",
		});
  
		setErrors((prev) => ({
		  ...prev,
		  earningApi: "",
		  organizer: "",
		  dj: "",
		  admin: "",
		}));
	  } else {
		setErrors((prev) => ({
		  ...prev,
		  earningApi: response?.data?.message || "Something went wrong",
		}));
	  }
	  setLoadingEarning(false);
	};
  
	React.useEffect(() => {
	  loadReferral();
	  loadVote();
	  loadEarningForType("TIP");
	}, []);
  
	const handleSelectEarningType = async (type) => {
	  setEarningType(type);
	  setErrors((prev) => ({
		...prev,
		earningApi: "",
		organizer: "",
		dj: "",
		admin: "",
	  }));
	  await loadEarningForType(type);
	};
  
	const handleSaveEarning = async () => {
	  const { organizer, dj, admin } = earning;
  
	  const newErrors = {};
	  if (!isValidNumber(organizer)) newErrors.organizer = "Invalid number";
	  if (!isValidNumber(dj)) newErrors.dj = "Invalid number";
	  if (!isValidNumber(admin)) newErrors.admin = "Invalid number";
  
	  if (Object.keys(newErrors).length) {
		setErrors((prev) => ({ ...prev, ...newErrors }));
		return;
	  }
  
	  const idToPatch = earningIds?.[earningType];
	  if (!idToPatch) {
		setErrors((prev) => ({
		  ...prev,
		  earningApi: "Config id not found",
		}));
		return;
	  }
  
	  const payload = {
		type: earningType, // TIP or REVENUE
		djShare: Number(dj),
		organizerShare: Number(organizer),
		adminShare: Number(admin),
	  };
  
	  setLoadingEarningButton(true);
	  const response = await UpdateShareConfigRatio(idToPatch, payload);
	  setLoadingEarningButton(false);
  
	  if (response?.data?.success || response?.status === 200) {
		console.log("Updated Earning Payload:", payload);
		toast.success("Earning distribution updated successfully");
		setErrors((prev) => ({
		  ...prev,
		  earningApi: "",
		}));
		loadEarningForType(earningType);
	  } else {
		const apiErrors = response?.data?.errors || {};
		const firstKey = Object.keys(apiErrors)[0];
		const firstMsg =
		  firstKey &&
		  (apiErrors[firstKey]?.message ||
			apiErrors[firstKey]?.[0]?.message ||
			apiErrors[firstKey]?.[0] ||
			apiErrors[firstKey]);
  
		setErrors((prev) => ({
		  ...prev,
		  earningApi: firstMsg || response?.data?.message || "Something went wrong",
		}));
	  }
	};
  
	const handleSaveReferral = async () => {
	  if (!isValidNumber(referralAmount)) {
		setErrors((prev) => ({
		  ...prev,
		  referralAmount: "Please enter a valid number",
		}));
		return;
	  }
  
	  if (!referralId) {
		setErrors((prev) => ({
		  ...prev,
		  referralApi: "Config id not found",
		}));
		return;
	  }
  
	  const payload = {
		name: "Referral Amount",
		settingKey: "REFERRAL_AMOUNT",
		settingValue: Number(referralAmount),
		description: "Referral reward amount controlled by admin",
	  };
  
	  setLoadingReferralButton(true);
	  const response = await UpdateSystemSetting(referralId, payload);
	  setLoadingReferralButton(false);
  
	  if (response?.data?.success || response?.status === 200) {
		console.log("Updated Referral Payload:", payload);
		toast.success("Referral updated successfully");
		setErrors((prev) => ({
		  ...prev,
		  referralApi: "",
		}));
		loadReferral();
	  } else {
		const apiErrors = response?.data?.errors || {};
		const firstKey = Object.keys(apiErrors)[0];
		const firstMsg =
		  firstKey &&
		  (apiErrors[firstKey]?.message ||
			apiErrors[firstKey]?.[0]?.message ||
			apiErrors[firstKey]?.[0] ||
			apiErrors[firstKey]);
  
		setErrors((prev) => ({
		  ...prev,
		  referralApi: firstMsg || response?.data?.message || "Something went wrong",
		}));
	  }
	};
  
	const handleSaveVote = async () => {
	  if (!isValidNumber(voteUnit)) {
		setErrors((prev) => ({
		  ...prev,
		  voteUnit: "Please enter a valid number",
		}));
		return;
	  }
  
	  if (!voteId) {
		setErrors((prev) => ({
		  ...prev,
		  voteApi: "Config id not found",
		}));
		return;
	  }
  
	  const payload = {
		name: "Votes Per Dollar",
		settingKey: "VOTES_PER_DOLLAR",
		settingValue: Number(voteUnit),
		description: "Number of votes given for every 1 dollar",
	  };
  
	  setLoadingVoteButton(true);
	  const response = await UpdateSystemSetting(voteId, payload);
	  setLoadingVoteButton(false);
  
	  if (response?.data?.success || response?.status === 200) {
		console.log("Updated Vote Payload:", payload);
		toast.success("Vote updated successfully");
		setErrors((prev) => ({
		  ...prev,
		  voteApi: "",
		}));
		loadVote();
	  } else {
		const apiErrors = response?.data?.errors || {};
		const firstKey = Object.keys(apiErrors)[0];
		const firstMsg =
		  firstKey &&
		  (apiErrors[firstKey]?.message ||
			apiErrors[firstKey]?.[0]?.message ||
			apiErrors[firstKey]?.[0] ||
			apiErrors[firstKey]);
  
		setErrors((prev) => ({
		  ...prev,
		  voteApi: firstMsg || response?.data?.message || "Something went wrong",
		}));
	  }
	};
  
	return (
	  <>
		<Helmet>
		  <title> Setting</title>
		</Helmet>
  
		<div className="listingPages allAccountsPage">
		  <Box maxWidth={1600} mx={"auto"}>
			<div className="listingPagesHead">
			  <div className="left">
				<h3>Setting</h3>
			  </div>
			</div>
  
			<div className="listingMain">
			  <Box sx={{ borderBottom: 1, borderColor: "#0057FF" }}>
				<Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
				  <Tab label="Referral" {...a11yProps(0)} />
				  <Tab label="Vote" {...a11yProps(1)} />
				  <Tab label="Earning distribution" {...a11yProps(2)} />
				</Tabs>
			  </Box>
  
			  <CustomTabPanel value={value} index={0}>
				<Box maxWidth={600} mx={"auto"}>
				  {errors.referralApi && (
					<Typography sx={{ color: "red", mb: 2 }}>{errors.referralApi}</Typography>
				  )}
				  {loadingReferral && (
					<Typography sx={{ color: "white", mb: 2 }}>{"...Loading "}</Typography>
				  )}
  
				  <Grid container spacing={2}>
					<Grid item xs={12}>
					  <div className="formGroup">
						<label className="text-white mb-2">Referral Amount</label>
						<div className="inputgroup inputgroupRight">
						  <input
							type="text"
							id="phoneNo"
							// value={state.phoneNo}
							// onChange={onChangeGetDataAllData}
							name="phoneNo"
							// placeholder="insert phone number"
							className="form-control form-control-v1"
							value={referralAmount}
							onChange={(e) => {
							  setReferralAmount(e.target.value);
							  setErrors((prev) => ({
								...prev,
								referralAmount: "",
								referralApi: "",
							  }));
							}}
						  />
						</div>
  
						{errors.referralAmount && (
						  <small style={{ color: "red" }}>{errors.referralAmount}</small>
						)}
					  </div>
					</Grid>
					<Grid item xs={12}>
					  <button
						className="themeBtn fullBtn"
						onClick={handleSaveReferral}
						disabled={loadingReferralButton}
					  >
						{loadingReferralButton ? "Loading..." : "Save"}
					  </button>
					</Grid>
				  </Grid>
				</Box>
			  </CustomTabPanel>
  
			  <CustomTabPanel value={value} index={1}>
				<Box maxWidth={600} mx={"auto"}>
				  {errors.voteApi && (
					<Typography sx={{ color: "red", mb: 2 }}>{errors.voteApi}</Typography>
				  )}
				  {loadingVote && (
					<Typography sx={{ color: "white", mb: 2 }}>{"...Loading "}</Typography>
				  )}
  
				  <Grid container spacing={2}>
					<Grid item xs={12}>
					  <div className="formGroup">
						<label className="text-white mb-2">Vote Unit (for 1 dollar)</label>
						<div className="inputgroup">
						  <input
							type="text"
							id="phoneNo"
							// value={state.phoneNo}
							// onChange={onChangeGetDataAllData}
							name="phoneNo"
							// placeholder="insert phone number"
							className="form-control form-control-v1"
							value={voteUnit}
							onChange={(e) => {
							  setVoteUnit(e.target.value);
							  setErrors((prev) => ({
								...prev,
								voteUnit: "",
								voteApi: "",
							  }));
							}}
						  />
						</div>
  
						{errors.voteUnit && (
						  <small style={{ color: "red" }}>{errors.voteUnit}</small>
						)}
					  </div>
					</Grid>
					<Grid item xs={12}>
					  <button
						className="themeBtn fullBtn"
						onClick={handleSaveVote}
						disabled={loadingVoteButton}
					  >
						{loadingVoteButton ? "Loading..." : "Save"}
					  </button>
					</Grid>
				  </Grid>
				</Box>
			  </CustomTabPanel>
  
			  <CustomTabPanel value={value} index={2}>
				<Box maxWidth={600} mx={"auto"}>
				  <Grid container spacing={1} alignItems={"center"} mb={3}>
					<Grid item width={220}>
					  <button
						className={`${earningType === "TIP" ? "themeBtn" : "themeOutlineBtn"} fullBtn`}
						onClick={() => handleSelectEarningType("TIP")}
						disabled={loadingEarning && earningType === "TIP"}
					  >
						Tips Percentage
					  </button>
					</Grid>
					<Grid item width={220}>
					  <button
						className={`${earningType === "REVENUE" ? "themeBtn" : "themeOutlineBtn"} fullBtn`}
						onClick={() => handleSelectEarningType("REVENUE")}
						disabled={loadingEarning && earningType === "REVENUE"}
					  >
						Event Earning Percentage
					  </button>
					</Grid>
				  </Grid>
  
				  {errors.earningApi && (
					<Typography sx={{ color: "red", mb: 2 }}>{errors.earningApi}</Typography>
				  )}
				  {loadingEarning && (
					<Typography sx={{ color: "white", mb: 2 }}>{"...Loading "}</Typography>
				  )}
  
				  <Grid container spacing={2}>
					<Grid item xs={12}>
					  <div className="formGroup">
						<label className="text-white mb-2">Event organizer</label>
						<div className="inputgroup">
						  <input
							type="text"
							id="phoneNo"
							// value={state.phoneNo}
							// onChange={onChangeGetDataAllData}
							name="phoneNo"
							// placeholder="insert phone number"
							className="form-control form-control-v1"
							value={earning.organizer}
							onChange={(e) => {
							  setEarning({ ...earning, organizer: e.target.value });
							  setErrors((prev) => ({ ...prev, organizer: "" }));
							}}
						  />
						</div>
  
						{errors.organizer && (
						  <small style={{ color: "red" }}>{errors.organizer}</small>
						)}
					  </div>
					</Grid>
  
					<Grid item xs={12}>
					  <div className="formGroup">
						<label className="text-white mb-2">Dj</label>
						<div className="inputgroup">
						  <input
							type="text"
							id="phoneNo"
							// value={state.phoneNo}
							// onChange={onChangeGetDataAllData}
							name="phoneNo"
							// placeholder="insert phone number"
							className="form-control form-control-v1"
							value={earning.dj}
							onChange={(e) => {
							  setEarning({ ...earning, dj: e.target.value });
							  setErrors((prev) => ({ ...prev, dj: "" }));
							}}
						  />
						</div>
  
						{errors.dj && <small style={{ color: "red" }}>{errors.dj}</small>}
					  </div>
					</Grid>
  
					<Grid item xs={12}>
					  <div className="formGroup">
						<label className="text-white mb-2">Admin</label>
						<div className="inputgroup">
						  <input
							type="text"
							id="phoneNo"
							// value={state.phoneNo}
							// onChange={onChangeGetDataAllData}
							name="phoneNo"
							// placeholder="insert phone number"
							className="form-control form-control-v1"
							value={earning.admin}
							onChange={(e) => {
							  setEarning({ ...earning, admin: e.target.value });
							  setErrors((prev) => ({ ...prev, admin: "" }));
							}}
						  />
						</div>
  
						{errors.admin && (
						  <small style={{ color: "red" }}>{errors.admin}</small>
						)}
					  </div>
					</Grid>
  
					<Grid item xs={12}>
					  <button
						className="themeBtn fullBtn"
						onClick={handleSaveEarning}
						disabled={loadingEarningButton}
					  >
						{loadingEarningButton ? "Loading..." : "Save"}
					  </button>
					</Grid>
				  </Grid>
				</Box>
			  </CustomTabPanel>
			</div>
		  </Box>
		</div>
	  </>
	);
  }
  